import { useEffect, useState } from "react";
import {
  AlertTriangle,
  LogOut,
  Server,
  Tractor,
  ClipboardList,
  Building2,
  Check,
  Plus,
  LogIn,
  Settings,
  ChevronDown,
  Ruler,
  Palette,
  Compass,
  Sparkles,
  GraduationCap,
  KeyRound,
} from "lucide-react";
import { startLearnAiModel } from "../features/wizard/learnAiModel";
import { useAuthStore } from "../stores/authStore";
import { useUiStore } from "../stores/uiStore";
import { useBulkErrorsBadge, useMe } from "../hooks/queries";
import { useOrgStore } from "../stores/orgStore";
import { useUiMode } from "../stores/uiModeStore";
import { useEnhancedVisuals } from "../stores/enhancedVisualsStore";
import { useJdConnect } from "../hooks/useJdConnect";
import { leaveOrgAction } from "../features/org/orgActions";
import { reportSwallowed } from "../lib/reportSwallowed";
import { useSoilUnits } from "../stores/soilUnitsStore";
import { isDefaultUnits } from "../lib/soilUnits";
import { SoilUnitsPanel } from "../features/soil-units/SoilUnitsPanel";
import { BrandingModal } from "../features/branding/BrandingModal";
import { NotificationSettings } from "../features/account/NotificationSettings";
import { ChangePasswordDialog } from "../features/sign-in/ChangePasswordDialog";
import {
  LinkOrgModal,
  CreateOrgModal,
  ManageOrgModal,
} from "../features/org/OrgDialogs";
import { api } from "../api";
import { passwordChangeEnabled } from "../lib/passwordChangeFlag";
import { Popover } from "../ui/Popover";
import type { OrgMembership } from "../types/contract";

// Header profile menu + organization hub: the signed-in user, the ACTIVE org with a switcher across
// their linked orgs, link (by join code) / create / leave / manage, plus work orders John Deere
// Ops, and log out.
export function ProfileMenu() {
  const enhancedVisuals = useEnhancedVisuals((st) => st.enabled);
  const uiMode = useUiMode((s) => s.mode);
  const setUiMode = useUiMode((s) => s.setMode);
  const toggleEnhanced = useEnhancedVisuals((st) => st.toggle);
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState<"link" | "create" | null>(null);
  const [manage, setManage] = useState<OrgMembership | null>(null);
  const [confirmLeave, setConfirmLeave] = useState<string | null>(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [leaveErr, setLeaveErr] = useState<string | null>(null);

  const session = useAuthStore((s) => s.session);
  const logout = useAuthStore((s) => s.logout);
  const openWorkOrders = useUiStore((s) => s.openWorkOrders);
  const openErrorsMenu = useUiStore((s) => s.openErrorsMenu);
  const [unitsOpen, setUnitsOpen] = useState(false);
  const [brandingOpen, setBrandingOpen] = useState(false);
  const unitsAreDefault = useSoilUnits((s) =>
    isDefaultUnits({
      depthIn: s.depthIn,
      pExtractant: s.pExtractant,
      kExtractant: s.kExtractant,
      massBasis: s.massBasis,
    }),
  );
  const openSystemDashboard = useUiStore((s) => s.openSystemDashboard);
  // from /users/me, NOT the login session -- the flag is read per-request server-side (see MeRecord)
  const me = useMe(!!session);
  const isGlobalAdmin = !!me.data?.is_global_admin;
  const { data: badge } = useBulkErrorsBadge(!!session);
  const orgs = useOrgStore((s) => s.orgs);
  const activeOrgId = useOrgStore((s) => s.activeOrgId);
  const switchOrg = useOrgStore((s) => s.switchOrg);
  const { status, refresh, connect, disconnect, connecting, busy } =
    useJdConnect();

  const activeOrg = orgs.find((o) => o.org_id === activeOrgId) ?? null;

  useEffect(() => {
    if (open)
      void refresh().catch((err) =>
        reportSwallowed("refresh John Deere status", err),
      );
  }, [open, refresh]);

  const close = () => {
    setOpen(false);
    setConfirmLeave(null);
    setLeaveErr(null);
  };
  async function doLeave(orgId: string) {
    setLeaving(true);
    setLeaveErr(null);
    try {
      await leaveOrgAction(orgId);
      setConfirmLeave(null); // success — clear the confirm row (on failure it STAYS so the error shows)
    } catch (e) {
      setLeaveErr(
        e instanceof Error ? e.message : "Could not leave the organization.",
      );
    } finally {
      setLeaving(false);
    }
  }

  return (
    <div className="relative">
      <Popover
        scrim
        open={open}
        onOpenChange={(next) => {
          if (next) setOpen(true);
          else close();
        }}
        className="z-menu w-72 rounded-md border border-line bg-raised py-1 text-sm text-fg shadow-lg"
        trigger={
          <button
            className="flex max-w-[10rem] items-center gap-1 rounded-full border border-line bg-raised px-2 py-1 text-[11px] text-fg hover:bg-hover"
            aria-label="Active organization"
            title={
              activeOrg ? `Organization: ${activeOrg.name}` : "No organization"
            }
          >
            <Building2 size={12} className="shrink-0 text-muted" />
            <span className="truncate">
              {activeOrg?.name ?? "No organization"}
            </span>
            <ChevronDown size={11} className="shrink-0 text-faint" />
          </button>
        }
      >
        <div className="border-b border-line px-3 py-2">
          <div className="truncate font-medium text-fg">{session?.email}</div>
          <div className="text-[11px] text-faint">
            {activeOrg
              ? `${activeOrg.name} · ${activeOrg.role === "org_admin" ? "admin" : "member"}`
              : "no active organization"}
          </div>
        </div>

        {/* Organizations */}
        <div className="border-b border-line px-3 py-2">
          <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-muted">
            <Building2 size={13} /> Organizations
          </div>
          <ul className="mb-1 flex flex-col">
            {orgs.map((o) => (
              <li key={o.org_id}>
                {confirmLeave === o.org_id ? (
                  <div className="rounded bg-hover px-1.5 py-1 text-[11px]">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-fg">
                        Leave “{o.name}”?
                      </span>
                      <span className="flex shrink-0 gap-1">
                        <button
                          className="rounded px-1.5 py-0.5 text-red-400 hover:bg-red-400/10 disabled:opacity-50"
                          disabled={leaving}
                          onClick={() => void doLeave(o.org_id)}
                        >
                          {leaving ? "…" : "Leave"}
                        </button>
                        <button
                          className="rounded px-1.5 py-0.5 text-muted hover:bg-hover"
                          onClick={() => {
                            setConfirmLeave(null);
                            setLeaveErr(null);
                          }}
                        >
                          Cancel
                        </button>
                      </span>
                    </div>
                    {leaveErr && (
                      <p className="mt-1 text-[10px] text-red-400">
                        {leaveErr}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="group flex items-center gap-1 rounded px-1.5 py-1 hover:bg-hover">
                    <button
                      className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
                      onClick={() => {
                        switchOrg(o.org_id);
                        close();
                      }}
                    >
                      {o.org_id === activeOrgId ? (
                        <Check size={13} className="shrink-0 text-accent" />
                      ) : (
                        <span className="w-[13px] shrink-0" />
                      )}
                      <span className="truncate text-[12px] text-fg">
                        {o.name}
                      </span>
                      {o.role === "org_admin" && (
                        <span className="shrink-0 text-[9px] uppercase tracking-wide text-faint">
                          admin
                        </span>
                      )}
                    </button>
                    {o.role === "org_admin" && (
                      <button
                        className="rounded p-1 text-faint hover:text-fg"
                        aria-label={`Manage ${o.name}`}
                        onClick={() => {
                          setManage(o);
                          close();
                        }}
                      >
                        <Settings size={12} />
                      </button>
                    )}
                    <button
                      className="rounded px-1 py-0.5 text-[10px] text-faint hover:text-red-400"
                      onClick={() => setConfirmLeave(o.org_id)}
                    >
                      Leave
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
          <div className="flex flex-col gap-0.5">
            <button
              className="flex items-center gap-1.5 rounded px-1.5 py-1 text-left text-[12px] text-fg hover:bg-hover"
              onClick={() => {
                setModal("link");
                close();
              }}
            >
              <LogIn size={13} className="text-muted" /> Link an organization
            </button>
            <button
              className="flex items-center gap-1.5 rounded px-1.5 py-1 text-left text-[12px] text-fg hover:bg-hover"
              onClick={() => {
                setModal("create");
                close();
              }}
            >
              <Plus size={13} className="text-muted" /> Create organization
            </button>
          </div>
        </div>

        <button
          className="flex w-full items-center gap-2 border-b border-line px-3 py-2 text-left text-xs text-fg hover:bg-hover"
          onClick={() => {
            close();
            openWorkOrders();
          }}
        >
          <ClipboardList size={13} /> Work orders
        </button>

        {/* Hiding this is UX, not security: every admin endpoint verifies the caller server-side, so a flipped flag yields a shell of 403s, not data. */}
        {isGlobalAdmin && (
          <button
            className="flex w-full items-center gap-2 border-b border-line px-3 py-2 text-left text-xs text-fg hover:bg-hover"
            onClick={() => {
              close();
              openSystemDashboard();
            }}
          >
            <Server size={13} /> System Dashboard
          </button>
        )}
        {/*
 REV 4: the append-only record of what bulk processing couldn't import. Badge = errors since
                last open (mark-seen bookmark); count null or read failure = NO badge, never a confident 0.
*/}
        <button
          className="flex w-full items-center gap-2 border-b border-line px-3 py-2 text-left text-xs text-fg hover:bg-hover"
          onClick={() => {
            close();
            openErrorsMenu();
          }}
        >
          <AlertTriangle size={13} className="text-amber-500" /> Errors
          {typeof badge?.count === "number" && badge.count > 0 && (
            <span className="ml-auto rounded-full bg-amber-500/20 px-1.5 text-[10px] tabular-nums text-amber-500">
              {badge.count}
            </span>
          )}
        </button>
        {/* Soil data units: a DISPLAY lens — pick the depth/extractants you want to read values in. */}
        <button
          className="flex w-full items-center gap-2 border-b border-line px-3 py-2 text-left text-xs text-fg hover:bg-hover"
          onClick={() => {
            close();
            setUnitsOpen(true);
          }}
        >
          <Ruler size={13} /> Soil data units
          {!unitsAreDefault && (
            <span className="ml-auto text-[10px] text-amber-500">custom</span>
          )}
        </button>
        {/* EASY MODE — Easy hides the layer-detail toggle (only Easy layers show) and hides the customization / full-control options for maps and Rx. RADIOS, NOT A SWITCH, and this one matters more than the house style: "Advanced" is not the negation of "Easy". */}
        <div className="border-b border-line px-3 py-2">
          <div className="mb-1.5 flex items-center gap-2 text-xs text-fg">
            <Compass size={13} /> Mode
          </div>
          <div className="grid grid-cols-2 gap-2">
            {(["easy", "advanced"] as const).map((m) => (
              <button
                key={m}
                type="button"
                aria-pressed={uiMode === m}
                onClick={() => setUiMode(m)}
                className={`rounded border px-2 py-1.5 text-xs ${
                  uiMode === m
                    ? "border-accent bg-accent/10 text-fg"
                    : "border-line text-muted hover:bg-hover"
                }`}
              >
                {m === "easy" ? "Easy Mode" : "Advanced Mode"}
              </button>
            ))}
          </div>
        </div>
        {/* DEFAULTS ON: the animation exists for the user who has never touched this, and "off" is a preference formed after seeing it. Turning it off changes NOTHING about what runs — only whether the window appears. */}
        <button
          className="flex w-full items-center gap-2 border-b border-line px-3 py-2 text-left text-xs text-fg hover:bg-hover"
          onClick={() => toggleEnhanced()}
          role="switch"
          aria-checked={enhancedVisuals}
        >
          <Sparkles size={13} /> Enhanced Visuals
          <span
            className={`ml-auto rounded px-1.5 text-[10px] font-medium ${enhancedVisuals ? "bg-accent/20 text-accent" : "bg-hover text-faint"}`}
          >
            {enhancedVisuals ? "On" : "Off"}
          </span>
        </button>
        <button
          className="flex w-full items-center gap-2 border-b border-line px-3 py-2 text-left text-xs text-fg hover:bg-hover"
          onClick={() => {
            close();
            startLearnAiModel();
          }}
        >
          <GraduationCap size={13} /> Genius Rx Demo/Tutorial
        </button>
        {/* REPORT BRANDING: logo/name/address for the Branded Section 180 report. */}
        <button
          className="flex w-full items-center gap-2 border-b border-line px-3 py-2 text-left text-xs text-fg hover:bg-hover"
          onClick={() => {
            close();
            setBrandingOpen(true);
          }}
        >
          <Palette size={13} /> Add Branding
        </button>
        {/* The two Bulk Data Dump entries moved to "Add a field" on the main screen one door per flow, chosen by what you're adding rather than by how many. */}

        <div className="border-b border-line px-3 py-2">
          <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-muted">
            <Tractor size={13} /> John Deere Ops
          </div>
          {!status && <p className="text-[11px] text-faint">checking…</p>}
          {status && !status.configured && (
            <p className="text-[11px] text-faint">Not available yet.</p>
          )}
          {status &&
            status.configured &&
            (status.connected ? (
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-emerald-400">Connected</span>
                <button
                  disabled={busy}
                  className="text-xs text-red-400 hover:underline disabled:opacity-50"
                  onClick={() => void disconnect()}
                >
                  {busy ? "Disconnecting…" : "Disconnect"}
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-faint">Not connected</span>
                <button
                  disabled={connecting}
                  className="text-xs text-fg hover:underline disabled:opacity-50"
                  onClick={connect}
                >
                  {connecting ? "Connecting…" : "Connect"}
                </button>
              </div>
            ))}
        </div>

        <button
          className="flex w-full items-center gap-2 border-b border-line px-3 py-2 text-left text-xs text-fg hover:bg-hover"
          onClick={() => {
            close();
            setSettingsOpen(true);
          }}
        >
          <Settings size={13} /> Settings
        </button>
        {passwordChangeEnabled() && (
          <button
            className="flex w-full items-center gap-2 border-b border-line px-3 py-2 text-left text-xs text-fg hover:bg-hover"
            onClick={() => {
              close();
              setChangePasswordOpen(true);
            }}
          >
            <KeyRound size={13} /> Change password
          </button>
        )}
        <button
          className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-fg hover:bg-hover"
          onClick={() => {
            close();
            void logout();
          }}
        >
          <LogOut size={13} /> Log out
        </button>
      </Popover>

      {modal === "link" && <LinkOrgModal onClose={() => setModal(null)} />}
      {modal === "create" && <CreateOrgModal onClose={() => setModal(null)} />}
      {manage && (
        <ManageOrgModal org={manage} onClose={() => setManage(null)} />
      )}
      {unitsOpen && <SoilUnitsPanel onClose={() => setUnitsOpen(false)} />}
      {brandingOpen && <BrandingModal onClose={() => setBrandingOpen(false)} />}
      {passwordChangeEnabled() && changePasswordOpen && session?.email && (
        <ChangePasswordDialog
          email={session.email}
          onClose={() => setChangePasswordOpen(false)}
          onSend={(address) => api.requestPasswordReset(address)}
        />
      )}
      {settingsOpen && (
        <NotificationSettings onClose={() => setSettingsOpen(false)} />
      )}
    </div>
  );
}
